import { Component, EventEmitter, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  AdvancedOptionsBase,
  AdvancedOptionsParams,
} from '@helios/filter-modal'
import { AdvancedFilters } from '@helios/generic-components'
import { splitData } from '@helios/utils'
import { IntegerIdListValidator } from '@helios/validators'
import { DisplayDateFormat } from '@northfork/basics'
import { set, unset } from 'lodash-es'
import { tap } from 'rxjs/operators'
import { AdvancedFilterContext } from '../services/account-list.service'
import { RadioButtonOptions } from './radio-group-filter/radio-group-filter.component'
import { SecurityFilter } from './security-filter/security-filter.component'

@Component({
  selector: 'hs-advanced-filters',
  templateUrl: './advanced-filters.component.html',
  styleUrls: ['./advanced-filters.component.scss'],
})
export class AdvancedFiltersComponent
  extends AdvancedOptionsBase<AdvancedFilterContext | undefined | null>
  implements OnInit
{
  set advancedOptionsParams(
    value:
      | AdvancedOptionsParams<
          AdvancedFilterContext | null | undefined,
          { [key: string]: unknown }
        >
      | undefined,
  ) {
    this.context = value?.context
  }
  context: AdvancedFilterContext | null | undefined
  onChange = new EventEmitter<
    AdvancedOptionsParams<AdvancedFilterContext | null | undefined>
  >()

  parentClientIdForm: FormControl = new FormControl('', IntegerIdListValidator)
  aggregateIdForm: FormControl = new FormControl('', IntegerIdListValidator)
  isReconciledForm: FormControl = new FormControl(RadioButtonOptions.BOTH)
  isTradingReconciledForm: FormControl = new FormControl(
    RadioButtonOptions.BOTH,
  )
  hasOmegaDateForm: FormControl = new FormControl(RadioButtonOptions.BOTH)
  hasWaitingIssuesForm: FormControl = new FormControl(RadioButtonOptions.BOTH)
  hasFoundIssuesForm: FormControl = new FormControl(RadioButtonOptions.BOTH)
  hasTradingReconTimestampForm: FormControl = new FormControl(
    RadioButtonOptions.BOTH,
  )
  includeNonReconcilingAccounts: FormControl = new FormControl(
    RadioButtonOptions.NO,
  )
  reconDateForm: FormControl = new FormControl('')
  dateFormat = DisplayDateFormat

  isApplyDisabled = false

  private readonly advancedFiltersValidationMap: Map<
    keyof AdvancedFilters,
    boolean
  > = new Map([
    ['parentClientId', true],
    ['aggregateId', true],
    ['security', true],
  ])

  ngOnInit(): void {
    this.initFormControls()
  }

  private initFormControls() {
    if (this.context?.advancedFilters) {
      this.parentClientIdForm.reset(
        this.context?.advancedFilters.parentClientId?.join(',') ?? '',
      )
      this.aggregateIdForm.reset(
        this.context?.advancedFilters.aggregateId?.join(',') ?? '',
      )
      this.isReconciledForm.reset(
        getRadioOptionFromFilter(this.context?.advancedFilters.isReconciled),
      )
      this.isTradingReconciledForm.reset(
        getRadioOptionFromFilter(
          this.context?.advancedFilters.isTradingReconciled,
        ),
      )
      this.hasOmegaDateForm.reset(
        getRadioOptionFromFilter(this.context?.advancedFilters.hasOmegaDate),
      )
      this.hasWaitingIssuesForm.reset(
        getRadioOptionFromFilter(
          this.context?.advancedFilters.hasWaitingIssues,
        ),
      )
      this.hasFoundIssuesForm.reset(
        getRadioOptionFromFilter(this.context?.advancedFilters.hasFoundIssues),
      )
      this.hasTradingReconTimestampForm.reset(
        getRadioOptionFromFilter(
          this.context?.advancedFilters.hasTradingReconTimestamp,
        ),
      )
      this.includeNonReconcilingAccounts.reset(
        getRadioOptionFromFilter(
          this.context?.advancedFilters.includeNonReconciling,
          true,
        ),
      )

      this.reconDateForm.reset(this.context?.reconDate)

      const csvFiltersMap: Map<keyof AdvancedFilters, FormControl> = new Map([
        ['parentClientId', this.parentClientIdForm],
        ['aggregateId', this.aggregateIdForm],
      ])

      csvFiltersMap.forEach((form, filterKey) => {
        form.valueChanges
          .pipe(
            tap(value => {
              this.updateCsvFilter(value, filterKey, form.valid)
              this.updateApplyButton()
            }),
          )
          .subscribe()
      })

      const radioFiltersMap: Map<keyof AdvancedFilters, FormControl> = new Map([
        ['isReconciled', this.isReconciledForm],
        ['isTradingReconciled', this.isTradingReconciledForm],
        ['hasOmegaDate', this.hasOmegaDateForm],
        ['hasWaitingIssues', this.hasWaitingIssuesForm],
        ['hasFoundIssues', this.hasFoundIssuesForm],
        ['hasTradingReconTimestamp', this.hasTradingReconTimestampForm],
        ['includeNonReconciling', this.includeNonReconcilingAccounts],
      ])

      radioFiltersMap.forEach((form, filterKey) => {
        form.valueChanges
          .pipe(
            tap(value =>
              updateRadioAdvancedFilter(
                value,
                filterKey,
                this.context?.advancedFilters,
              ),
            ),
          )
          .subscribe()
      })

      this.reconDateForm.valueChanges
        .pipe(
          tap(value => {
            if (!this.context?.advancedFilters) {
              return
            }
            this.context.reconDate = value
          }),
        )
        .subscribe()
    }
  }

  private updateApplyButton() {
    this.isApplyDisabled = Array.from(
      this.advancedFiltersValidationMap.values(),
    ).includes(false)
  }

  private updateCsvFilter(
    value: string,
    key: keyof AdvancedFilters,
    valid: boolean,
  ) {
    this.advancedFiltersValidationMap.set(key, valid)
    if (valid && this.context?.advancedFilters) {
      const values = splitData(value).map(s => parseInt(s))
      if (values.length === 0) unset(this.context?.advancedFilters, key)
      else set(this.context?.advancedFilters, key, values)
    }
  }

  updateSecurityFilter(securityFilter: SecurityFilter) {
    const securityIdentifier = 'security'
    this.advancedFiltersValidationMap.set(
      securityIdentifier,
      securityFilter.valid,
    )
    if (securityFilter.valid && this.context?.advancedFilters) {
      if (securityFilter.security === '')
        unset(this.context?.advancedFilters, securityIdentifier)
      else
        set(this.context?.advancedFilters, securityIdentifier, {
          securityIdentifier: securityFilter.security,
          currentlyHeld: securityFilter.currentlyHeld,
        })
    }
    this.updateApplyButton()
  }

  onApply() {
    return
  }
}

/**
 * update radio advanced filter
 */
export function updateRadioAdvancedFilter(
  value: RadioButtonOptions,
  key: keyof AdvancedFilters,
  advancedFilters?: AdvancedFilters,
): AdvancedFilters {
  if (advancedFilters == null) return {}

  if (value === RadioButtonOptions.YES) set(advancedFilters, key, true)
  else if (value === RadioButtonOptions.NO) set(advancedFilters, key, false)
  else unset(advancedFilters, key)

  return advancedFilters
}

/**
 * get radio option from filter
 */
export function getRadioOptionFromFilter(
  bool: boolean | undefined,
  excludeBoth?: boolean,
) {
  if (bool === undefined && !excludeBoth) return RadioButtonOptions.BOTH
  else return bool ? RadioButtonOptions.YES : RadioButtonOptions.NO
}
